import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Badge,
} from "@tremor/react";

import Drawer from "../../components/Drawer";

import {
  TrashIcon,
  DocumentDuplicateIcon,
  CogIcon,
  SignalIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { SOURCE_LIST } from "./utils";

const APP_URL = process.env.REACT_APP_HOSTNAME || "app.zafron.dev";

const SourcesTable = ({ sources, onDelete, onUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [sourceToEdit, setSourceToEdit] = useState({});
  const getEndpoint = (source) => {
    return `https://${APP_URL}/api/ingress/${source.maskId}?apiKey=${source.apiKey}`;
  };

  const onCopy = (source) => {
    copyToClipboard(getEndpoint(source));
    setCopied(source._id);
    setTimeout(() => {
      setCopied(null);
    }, 3000);
  };

  const copyToClipboard = (value) => {
    navigator.clipboard.writeText(value);
    // You can add some visual feedback here if you like
  };

  const getProviderName = (provider) => {
    const source = SOURCE_LIST.find((source) => source.value === provider);
    return source ? source.name : provider;
  };

  const onOk = () => {
    setEditOpen(false);
  };

  const openDrawer = (source) => {
    setSourceToEdit(source);
    setEditOpen(true);
  };

  const handleSaveSource = async (updatedSource) => {
    if (onUpdate) {
      await onUpdate(updatedSource);
      // Update the local state to reflect changes
      setSourceToEdit(updatedSource);
    }
  };

  const getSourceStatus = (source) => {
    const eventCount = source.eventCount || 0;
    const lastEvent = source.lastEventAt || source.lastEvent || source.updatedAt;
    const now = new Date();
    const lastEventDate = lastEvent ? new Date(lastEvent) : null;
    const hoursSinceLastEvent = lastEventDate ? (now - lastEventDate) / (1000 * 60 * 60) : null;
    
    if (source.enabled === false) {
      return { status: 'Disabled', color: 'gray', icon: null };
    } else if (eventCount === 0 || (!lastEventDate && eventCount === 0)) {
      return { status: 'No Events', color: 'yellow', icon: ClockIcon };
    } else if (eventCount > 0 && !lastEventDate) {
      return { status: 'Active', color: 'green', icon: SignalIcon };
    } else if (hoursSinceLastEvent < 1) {
      return { status: 'Active', color: 'green', icon: SignalIcon };
    } else if (hoursSinceLastEvent < 24) {
      return { status: 'Recent', color: 'yellow', icon: ClockIcon };
    } else {
      return { status: 'Inactive', color: 'red', icon: null };
    }
  };

  const getProviderBadge = (provider) => {
    const providerInfo = SOURCE_LIST.find((p) => p.value === provider);
    const name = providerInfo ? providerInfo.name : provider;
    
    const colors = {
      'chirpstackv3': 'blue',
      'chirpstackv4': 'blue', 
      'ttnv3': 'green',
      'helium': 'purple'
    };
    
    return {
      name,
      color: colors[provider] || 'gray'
    };
  };

  const formatEventCount = (count) => {
    return new Intl.NumberFormat().format(count || 0);
  };

  const formatLastActivity = (source) => {
    const lastEvent = source.lastEventAt || source.lastEvent || source.updatedAt;
    if (!lastEvent) return 'Never';
    
    const date = new Date(lastEvent);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const minutes = Math.floor((now - date) / (1000 * 60));
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const days = Math.floor(diffInHours / 24);
      return `${days}d ago`;
    }
  };

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Source</TableHeaderCell>
            <TableHeaderCell>Provider</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Events</TableHeaderCell>
            <TableHeaderCell>Last Activity</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sources.map((source) => {
            const status = getSourceStatus(source);
            const provider = getProviderBadge(source.provider);
            
            return (
              <TableRow key={source._id} className="hover:bg-gray-50 transition-colors">
                <TableCell>
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => openDrawer(source)}
                      className="text-left font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                    >
                      {source.name}
                    </button>
                    {source.description && (
                      <span className="text-xs text-gray-500 truncate max-w-xs">
                        {source.description}
                      </span>
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge color={provider.color} size="sm">
                    {provider.name}
                  </Badge>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {status.icon && <status.icon className="h-4 w-4 text-gray-400" />}
                    <Badge color={status.color} size="sm">
                      {status.status}
                    </Badge>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{formatEventCount(source.eventCount)}</span>
                    <span className="text-xs text-gray-500">total events</span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <span className="text-sm text-gray-600">
                    {formatLastActivity(source)}
                  </span>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="light"
                      onClick={() => onCopy(source)}
                      size="xs"
                      icon={DocumentDuplicateIcon}
                      title="Copy endpoint URL"
                    >
                      {copied === source._id ? 'Copied!' : ''}
                    </Button>
                    <Button
                      variant="secondary"
                      color="blue"
                      size="xs"
                      icon={CogIcon}
                      onClick={() => openDrawer(source)}
                      title="Source settings"
                    >
                    </Button>
                    <Button
                      variant="secondary"
                      color="red"
                      size="xs"
                      onClick={() => onDelete(source._id)}
                      icon={TrashIcon}
                      title="Delete source"
                    >
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className="flex justify-center">
        <Drawer
          isOpen={editOpen}
          onClose={onOk}
          rowData={sourceToEdit}
          onDeleteEvent={onDelete}
          onSave={handleSaveSource}
        />
      </div>
    </>
  );
};

export default SourcesTable;
